import * as React from 'react';

import { cn } from '../utils/cn';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Label } from './label';
import { Badge } from './badge';

export type ThemeOption =
  | 'auto'
  | 'light'
  | 'dark'
  | 'midnight'
  | 'purple-dark'
  | 'bubblegum'
  | 'ocean'
  | 'forest'
  | 'aurora'
  | 'ember'
  | 'citrus'
  | 'sandstone'
  | 'slate'
  | 'glacier'
  | 'cyberpunk'
  | 'rainbow'
  | 'retro'
  | 'noir';

export interface ThemeConfig {
  value: ThemeOption;
  label: string;
  description?: string;
  preview: string;
  accent: string;
}

export const defaultThemes: ThemeConfig[] = [
  {
    value: 'auto',
    label: 'System default',
    description: 'Automatically matches your OS appearance',
    preview: 'linear-gradient(135deg, #0f172a 0%, #f8fafc 100%)',
    accent: '#6366f1'
  },
  {
    value: 'light',
    label: 'Daybreak',
    description: 'Soft neutrals with restrained blue accents',
    preview: 'linear-gradient(135deg, #f7f9fc 0%, #eff4f8 55%, #e7eef5 100%)',
    accent: '#005aa8'
  },
  {
    value: 'dark',
    label: 'Deep space',
    description: 'High contrast dark theme suited for low light',
    preview: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    accent: '#38bdf8'
  },
  {
    value: 'midnight',
    label: 'Midnight',
    description: 'Vibrant blues over a twilight canvas',
    preview: 'linear-gradient(135deg, #0f172a 0%, #312e81 100%)',
    accent: '#60a5fa'
  },
  {
    value: 'purple-dark',
    label: 'Neon dusk',
    description: 'Electric purple highlights for high energy',
    preview: 'linear-gradient(135deg, #2d1b69 0%, #6d28d9 100%)',
    accent: '#a855f7'
  },
  {
    value: 'bubblegum',
    label: 'Bubblegum',
    description: 'Playful pink gradient with warm contrasts',
    preview: 'linear-gradient(135deg, #f472b6 0%, #fb7185 100%)',
    accent: '#fb7185'
  },
  {
    value: 'ocean',
    label: 'Ocean',
    description: 'Sea greens and teals for calm views',
    preview: 'linear-gradient(135deg, #0f766e 0%, #38bdf8 100%)',
    accent: '#0d9488'
  },
  {
    value: 'forest',
    label: 'Forest',
    description: 'Earthy greens with rich contrast',
    preview: 'linear-gradient(135deg, #14532d 0%, #4ade80 100%)',
    accent: '#22c55e'
  },
  {
    value: 'aurora',
    label: 'Aurora',
    description: 'Glacial blues meeting nebula purples',
    preview: 'linear-gradient(135deg, #061024 0%, #1a2045 60%, #23d1ee 100%)',
    accent: '#22d3ee'
  },
  {
    value: 'ember',
    label: 'Ember',
    description: 'Molten oranges and ember reds for night owls',
    preview: 'linear-gradient(135deg, #2b0900 0%, #bf360c 100%)',
    accent: '#f97316'
  },
  {
    value: 'citrus',
    label: 'Citrus',
    description: 'Bright citrus zest with sunny highlights',
    preview: 'linear-gradient(135deg, #fff7c2 0%, #facc15 100%)',
    accent: '#facc15'
  },
  {
    value: 'sandstone',
    label: 'Sandstone',
    description: 'Warm sandstone neutrals with copper accents',
    preview: 'linear-gradient(135deg, #f8e5d3 0%, #d97706 100%)',
    accent: '#d97706'
  },
  {
    value: 'slate',
    label: 'Midnight slate',
    description: 'Graphite panels and misty grays',
    preview: 'linear-gradient(135deg, #0d1117 0%, #273446 100%)',
    accent: '#94a3b8'
  },
  {
    value: 'glacier',
    label: 'Glacier',
    description: 'Icy blues and crisp whites',
    preview: 'linear-gradient(135deg, #f0f7ff 0%, #38bdf8 100%)',
    accent: '#38bdf8'
  },
  {
    value: 'cyberpunk',
    label: 'Cyberpunk',
    description: 'Neon magenta and aqua on a midnight backdrop',
    preview: 'linear-gradient(135deg, #080016 0%, #470068 60%, #22d3ee 100%)',
    accent: '#f472b6'
  },
  {
    value: 'rainbow',
    label: 'Rainbow pulse',
    description: 'Animated rainbow rail with playful surfaces',
    preview: 'linear-gradient(135deg, #f472b6 0%, #facc15 20%, #4ade80 50%, #38bdf8 80%, #a855f7 100%)',
    accent: '#a855f7'
  },
  {
    value: 'retro',
    label: 'Retro pop',
    description: '80s sorbet gradients with teal sparks',
    preview: 'linear-gradient(135deg, #ffe2c7 0%, #ff8a4c 100%)',
    accent: '#ff8a4c'
  },
  {
    value: 'noir',
    label: 'Noir',
    description: 'High-contrast monochrome with subtle glow',
    preview: 'linear-gradient(135deg, #050505 0%, #1f1f1f 100%)',
    accent: '#f5f5f5'
  }
];

export interface ThemeSelectorProps {
  value: ThemeOption;
  onValueChange: (theme: ThemeOption) => void;
  themes?: ThemeConfig[];
  label?: string;
  variant?: 'simple' | 'cards';
  disabled?: boolean;
  activeValue?: ThemeOption;
}

export function ThemeSelector({
  value,
  onValueChange,
  themes = defaultThemes,
  label = 'Theme',
  variant = 'simple',
  disabled = false,
  activeValue
}: ThemeSelectorProps) {
  if (variant === 'cards') {
    return (
      <div className="space-y-3">
        {label ? <Label className="text-base font-semibold">{label}</Label> : null}
        <div className="grid gap-4 md:grid-cols-2">
          {themes.map(theme => {
            const isSelected = theme.value === value;
            const isActive = activeValue !== undefined && theme.value === activeValue && !isSelected;
            return (
              <button
                key={theme.value}
                type="button"
                className={cn(
                  'flex items-start gap-4 rounded-xl border border-border bg-card p-4 text-left transition hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
                  isSelected && 'border-primary shadow-lg'
                )}
                onClick={() => onValueChange(theme.value)}
                disabled={disabled}
                aria-pressed={isSelected}
              >
                <span
                  className="h-14 w-14 flex-shrink-0 rounded-lg border-2 border-transparent"
                  style={{ background: theme.preview, borderColor: theme.accent }}
                  aria-hidden
                />
                <span className="flex flex-1 flex-col gap-1">
                  <span className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    {theme.label}
                    {isActive ? <Badge variant="outline">Active</Badge> : null}
                  </span>
                  {theme.description ? (
                    <span className="text-sm text-muted-foreground">{theme.description}</span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {label ? <Label htmlFor="theme-select">{label}</Label> : null}
      <Select value={value} onValueChange={onValueChange} disabled={disabled}>
        <SelectTrigger id="theme-select" className="w-full sm:w-64">
          <SelectValue placeholder="Select theme" />
        </SelectTrigger>
        <SelectContent>
          {themes.map(theme => (
            <SelectItem key={theme.value} value={theme.value}>
              {theme.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
